import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { createStaffUser } from '../../services/authService';
import { getAllUsers, setUserBlocked, setUserBlogAccess, setUserRole } from '../../services/userService';
import {
  getAllPartners,
  createPartner,
  updatePartner,
  deletePartner,
  createCertificationType,
  updateCertificationType,
  deleteCertificationType,
} from '../../services/partnerService';
import {
  getAllCertifications,
  createCertification,
  updateCertification,
  deleteCertification,
} from '../../services/certificationService';
import './admin.css';

const statusBadgeClass = (status) => {
  if (status === 'EXPIRED') return 'ap-badge ap-badge--danger';
  if (status === 'EXPIRING_SOON') return 'ap-badge ap-badge--amber';
  return 'ap-badge ap-badge--active'; // ACTIVE
};

export default function AdminPanel() {
  const [tab, setTab] = useState('createStaff'); // createStaff | users | partners | blogs | support

  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  // create staff
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('SUPPORT');
  const [busy, setBusy] = useState(false);

  // users
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('');

  // partners + certifications
  const [partners, setPartners] = useState([]);
  const [certifications, setCertifications] = useState([]);
  const [memberUsers, setMemberUsers] = useState([]);
  const [partnersBusy, setPartnersBusy] = useState(false);
  const [expandedPartnerId, setExpandedPartnerId] = useState(null);

  const [adminMemberId, setAdminMemberId] = useState('');
  const [adminCertTypeId, setAdminCertTypeId] = useState('');
  const [adminCompletedDate, setAdminCompletedDate] = useState('');
  const [editingMemberCertId, setEditingMemberCertId] = useState(null);
  const [editMemberCertDate, setEditMemberCertDate] = useState('');

  const [newPartnerName, setNewPartnerName] = useState('');
  const [newPartnerLogo, setNewPartnerLogo] = useState('');
  const [newPartnerPath, setNewPartnerPath] = useState('');

  const [certTypeFormFor, setCertTypeFormFor] = useState(null); // partnerId currently adding a cert type
  const [newCertName, setNewCertName] = useState('');
  const [newCertValidity, setNewCertValidity] = useState('');

  const [editingPartnerId, setEditingPartnerId] = useState(null);
  const [editPartnerName, setEditPartnerName] = useState('');
  const [editPartnerLogo, setEditPartnerLogo] = useState('');
  const [editPartnerPath, setEditPartnerPath] = useState('');
  const [editPartnerStatus, setEditPartnerStatus] = useState('ACTIVE');

  const [editingCertId, setEditingCertId] = useState(null);
  const [editCertName, setEditCertName] = useState('');
  const [editCertValidity, setEditCertValidity] = useState('');

  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  const loadUsers = async () => {
    setError('');
    try {
      const data = await getAllUsers(roleFilter || undefined);
      setUsers(data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load users');
    }
  };

  const loadPartnersAndCerts = async () => {
    setError('');
    try {
      const [partnersData, certsData, membersData] = await Promise.all([
        getAllPartners(),
        getAllCertifications(),
        getAllUsers('MEMBER'),
      ]);
      setPartners(partnersData);
      setCertifications(certsData);
      setMemberUsers((membersData || []).filter((u) => u.role === 'MEMBER'));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load partner data');
    }
  };

  useEffect(() => {
    if (tab === 'users') loadUsers();
    if (tab === 'partners') loadPartnersAndCerts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, roleFilter]);

  const onCreateStaff = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setBusy(true);
    try {
      const data = await createStaffUser({ email, password, role });
      setMsg(data?.message || 'Staff created');
      setEmail('');
      setPassword('');
      setRole('SUPPORT');
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to create staff');
    } finally {
      setBusy(false);
    }
  };

  const onToggleBlogAccess = async (u) => {
    setError('');
    try {
      await setUserBlogAccess(u.id, !u.canManageBlogs);
      await loadUsers();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update blog access');
    }
  };

  const onAddPartner = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      const data = await createPartner({
        name: newPartnerName,
        logo: newPartnerLogo || undefined,
        path: newPartnerPath || undefined,
      });
      setMsg(data?.message || 'Partner added');
      setNewPartnerName('');
      setNewPartnerLogo('');
      setNewPartnerPath('');
      await loadPartnersAndCerts();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to add partner');
    } finally {
      setPartnersBusy(false);
    }
  };

  const startEditPartner = (partner) => {
    setEditingPartnerId(partner.id);
    setEditPartnerName(partner.name || '');
    setEditPartnerLogo(partner.logo || '');
    setEditPartnerPath(partner.path || '');
    setEditPartnerStatus(partner.status || 'ACTIVE');
    setMsg('');
    setError('');
  };

  const cancelEditPartner = () => {
    setEditingPartnerId(null);
    setEditPartnerName('');
    setEditPartnerLogo('');
    setEditPartnerPath('');
    setEditPartnerStatus('ACTIVE');
  };

  const onUpdatePartner = async (e, partnerId) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      const data = await updatePartner(partnerId, {
        name: editPartnerName.trim(),
        logo: editPartnerLogo.trim() || null,
        path: editPartnerPath.trim() || null,
        status: editPartnerStatus,
      });
      setMsg(data?.message || 'Partner updated');
      cancelEditPartner();
      await loadPartnersAndCerts();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to update partner');
    } finally {
      setPartnersBusy(false);
    }
  };

  const startEditCert = (certType) => {
    setEditingCertId(certType.id);
    setEditCertName(certType.name || '');
    setEditCertValidity(
      certType.validityMonths === null || certType.validityMonths === undefined
        ? ''
        : String(certType.validityMonths)
    );
    setMsg('');
    setError('');
  };

  const cancelEditCert = () => {
    setEditingCertId(null);
    setEditCertName('');
    setEditCertValidity('');
  };

  const onUpdateCertType = async (e, certTypeId) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      const data = await updateCertificationType(certTypeId, {
        name: editCertName.trim(),
        validityMonths: editCertValidity ? Number(editCertValidity) : null,
      });
      setMsg(data?.message || 'Certification updated');
      cancelEditCert();
      await loadPartnersAndCerts();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to update certification');
    } finally {
      setPartnersBusy(false);
    }
  };

  const onDeletePartner = async (partnerId) => {
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      await deletePartner(partnerId);
      await loadPartnersAndCerts();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete partner');
    } finally {
      setPartnersBusy(false);
    }
  };

  const resetAdminCertificationForm = () => {
    setAdminMemberId('');
    setAdminCertTypeId('');
    setAdminCompletedDate('');
  };

  const onAdminAddCertification = async (e) => {
    e.preventDefault();
    setMsg('');
    setError('');

    if (!adminMemberId || !adminCertTypeId || !adminCompletedDate) {
      setError('Select a member, certification, and completion date.');
      return;
    }

    setPartnersBusy(true);
    try {
      const data = await createCertification({
        userId: adminMemberId,
        certificationTypeId: adminCertTypeId,
        completedDate: adminCompletedDate,
      });
      setMsg(data?.message || 'Member certification added');
      resetAdminCertificationForm();
      await loadPartnersAndCerts();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to add member certification');
    } finally {
      setPartnersBusy(false);
    }
  };

  const startEditMemberCertification = (certification) => {
    setEditingMemberCertId(certification.id);
    const date = new Date(certification.completedDate);
    setEditMemberCertDate(Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10));
    setMsg('');
    setError('');
  };

  const cancelEditMemberCertification = () => {
    setEditingMemberCertId(null);
    setEditMemberCertDate('');
  };

  const onUpdateMemberCertification = async (id) => {
    if (!editMemberCertDate) {
      setError('Select a completion date.');
      return;
    }

    setPartnersBusy(true);
    setMsg('');
    setError('');
    try {
      const data = await updateCertification(id, { completedDate: editMemberCertDate });
      setMsg(data?.message || 'Member certification updated');
      cancelEditMemberCertification();
      await loadPartnersAndCerts();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update member certification');
    } finally {
      setPartnersBusy(false);
    }
  };

  const onDeleteMemberCertification = async (certification) => {
    const label = certification?.certificationType?.name || 'this certification';
    if (!window.confirm(`Delete ${label} for ${certification?.user?.email || 'this member'}?`)) return;

    setPartnersBusy(true);
    setMsg('');
    setError('');
    try {
      const data = await deleteCertification(certification.id);
      if (editingMemberCertId === certification.id) cancelEditMemberCertification();
      setMsg(data?.message || 'Member certification deleted');
      await loadPartnersAndCerts();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete member certification');
    } finally {
      setPartnersBusy(false);
    }
  };

  const onAddCertType = async (e, partnerId) => {
    e.preventDefault();
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      await createCertificationType(partnerId, {
        name: newCertName,
        validityMonths: newCertValidity ? Number(newCertValidity) : undefined,
      });
      setNewCertName('');
      setNewCertValidity('');
      setCertTypeFormFor(null);
      await loadPartnersAndCerts();
    } catch (e2) {
      setError(e2?.response?.data?.message || 'Failed to add certification type');
    } finally {
      setPartnersBusy(false);
    }
  };

  const onDeleteCertType = async (certTypeId) => {
    setMsg('');
    setError('');
    setPartnersBusy(true);
    try {
      await deleteCertificationType(certTypeId);
      await loadPartnersAndCerts();
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to delete certification type');
    } finally {
      setPartnersBusy(false);
    }
  };

  const certsForType = (certTypeId) =>
    certifications.filter((c) => c.certificationTypeId === certTypeId);

  return (
    <div className="ap-page">
      <header className="ap-hero-wrap">
        <div className="ap-hero-bg" aria-hidden="true" />
        <div className="ap-hero-grid" aria-hidden="true" />
        <div className="ap-hero-inner">
          <div className="ap-eyebrow">
            <span className="ap-live-dot" aria-hidden="true" /> ADMIN CONSOLE
          </div>
          <h1>Admin Panel</h1>
          <p>Manage staff accounts, users and roles, the partner &amp; certification catalog, blogs, and support content — all from one place.</p>
        </div>
      </header>

      <div className="ap-shell">
        <div className="ap-tabs">
          <button
            className={`ap-tab ${tab === 'createStaff' ? 'is-active' : ''}`}
            onClick={() => setTab('createStaff')}
            disabled={busy}
            type="button"
          >
            Create staff
          </button>

          <button
            className={`ap-tab ${tab === 'users' ? 'is-active' : ''}`}
            onClick={() => setTab('users')}
            disabled={busy}
            type="button"
          >
            Users
          </button>

          <button
            className={`ap-tab ${tab === 'partners' ? 'is-active' : ''}`}
            onClick={() => setTab('partners')}
            disabled={busy}
            type="button"
          >
            Manage Partners and certifications
          </button>

          <button
            className={`ap-tab ${tab === 'blogs' ? 'is-active' : ''}`}
            onClick={() => setTab('blogs')}
            disabled={busy}
            type="button"
          >
            Blogs
          </button>

          <button
            className={`ap-tab ${tab === 'support' ? 'is-active' : ''}`}
            onClick={() => setTab('support')}
            disabled={busy}
            type="button"
          >
            Chat Support Content
          </button>
        </div>

        {msg ? <div className="ap-alert ap-alert--success">{msg}</div> : null}
        {error ? <div className="ap-alert ap-alert--error">{error}</div> : null}

        {tab === 'createStaff' ? (
          <section className="ap-card">
            <div className="ap-card-head">
              <h3 className="ap-card-title">Create Staff User</h3>
            </div>

            <form onSubmit={onCreateStaff} className="ap-form ap-form--narrow">
              <div className="ap-field">
                <label className="ap-label">Email</label>
                <input
                  className="ap-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  required
                  disabled={busy}
                />
              </div>

              <div className="ap-field">
                <label className="ap-label">Password</label>
                <input
                  className="ap-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  required
                  disabled={busy}
                />
              </div>

              <div className="ap-field">
                <label className="ap-label">Role</label>
                <select
                  className="ap-select"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={busy}
                >
                  <option value="SUPPORT">SUPPORT</option>
                  <option value="ADMIN_SUPPORT">ADMIN_SUPPORT</option>
                </select>
              </div>

              <div className="ap-form-actions">
                <button className="ap-btn ap-btn--primary" type="submit" disabled={busy}>
                  {busy ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </section>
        ) : null}

        {tab === 'users' ? (
          <section className="ap-card">
            <div className="ap-card-head">
              <h3 className="ap-card-title">All Users</h3>

              <div className="ap-card-actions">
                <select
                  className="ap-select"
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  disabled={busy}
                >
                  <option value="">All roles</option>
                  <option value="ADMIN">ADMIN</option>
                  <option value="ADMIN_SUPPORT">ADMIN_SUPPORT</option>
                  <option value="SUPPORT">SUPPORT</option>
                  <option value="MEMBER">MEMBER</option>
                  <option value="CUSTOMER">CUSTOMER</option>
                </select>

                <button className="ap-btn" type="button" onClick={loadUsers} disabled={busy}>
                  Refresh
                </button>
              </div>
            </div>

            <div className="ap-list">
              {users.map((u) => (
                <div key={u.id} className="ap-row">
                  <div className="ap-row-top">
                    <div>
                      <div className="ap-row-name">{u.email}</div>
                      <div className="ap-row-meta">
                        Role: {u.role} · Verified: {String(u.isVerified)} · Business: {u.business?.name || '—'}
                      </div>
                    </div>

                    <div className="ap-row-actions">
                      <span className={u.isBlocked ? 'ap-badge ap-badge--danger' : 'ap-badge ap-badge--active'}>
                        {u.isBlocked ? 'BLOCKED' : 'ACTIVE'}
                      </span>

                      {u.role !== 'ADMIN' ? (
                        <span className={u.canManageBlogs ? 'ap-badge ap-badge--amber' : 'ap-badge ap-badge--neutral'}>
                          {u.canManageBlogs ? 'BLOG ACCESS' : 'NO BLOG ACCESS'}
                        </span>
                      ) : null}

                      {isAdmin && u.role !== 'ADMIN' ? (
                        <select
                          className="ap-select"
                          value={u.role}
                          disabled={busy}
                          title="Change this user's role (they'll need to log out and back in for it to take effect)"
                          onChange={async (e) => {
                            const newRole = e.target.value;
                            if (newRole === u.role) return;
                            setError('');
                            try {
                              await setUserRole(u.id, newRole);
                              await loadUsers();
                            } catch (err) {
                              setError(err?.response?.data?.message || 'Failed to update role');
                            }
                          }}
                        >
                          <option value="CUSTOMER">CUSTOMER</option>
                          <option value="MEMBER">MEMBER</option>
                          <option value="SUPPORT">SUPPORT</option>
                          <option value="ADMIN_SUPPORT">ADMIN_SUPPORT</option>
                        </select>
                      ) : null}

                      {isAdmin ? (
                        <button
                          className={`ap-btn ${u.isBlocked ? '' : 'ap-btn--danger'}`}
                          type="button"
                          disabled={
                            busy ||
                            u.id === user?.id ||
                            u.role === 'ADMIN'
                          }
                          title={
                            u.id === user?.id
                              ? "You can't block your own account"
                              : u.role === 'ADMIN'
                              ? "You can't block an ADMIN account"
                              : ''
                          }
                          onClick={async () => {
                            setError('');
                            try {
                              await setUserBlocked(u.id, !u.isBlocked);
                              await loadUsers();
                            } catch (e) {
                              setError(e?.response?.data?.message || 'Failed to update user');
                            }
                          }}
                        >
                          {u.isBlocked ? 'Unblock' : 'Block'}
                        </button>
                      ) : null}

                      {isAdmin && u.role !== 'ADMIN' ? (
                        <button
                          className="ap-btn"
                          type="button"
                          disabled={busy}
                          onClick={() => onToggleBlogAccess(u)}
                        >
                          {u.canManageBlogs ? 'Revoke blog access' : 'Grant blog access'}
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}

              {users.length === 0 ? <div className="ap-empty">No users found.</div> : null}
            </div>
          </section>
        ) : null}

        {tab === 'partners' ? (
          <>
            {isAdmin ? (
              <section className="ap-card">
                <div className="ap-card-head">
                  <h3 className="ap-card-title">Add Partner</h3>
                </div>

                <form onSubmit={onAddPartner} className="ap-form ap-form--narrow">
                  <div className="ap-field">
                    <label className="ap-label">Partner name</label>
                    <input
                      className="ap-input"
                      value={newPartnerName}
                      onChange={(e) => setNewPartnerName(e.target.value)}
                      required
                      disabled={partnersBusy}
                    />
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Logo URL (optional)</label>
                    <input
                      className="ap-input"
                      value={newPartnerLogo}
                      onChange={(e) => setNewPartnerLogo(e.target.value)}
                      disabled={partnersBusy}
                    />
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Public page path (optional, e.g. /partners/Cisco)</label>
                    <input
                      className="ap-input"
                      value={newPartnerPath}
                      onChange={(e) => setNewPartnerPath(e.target.value)}
                      disabled={partnersBusy}
                    />
                  </div>

                  <div className="ap-form-actions">
                    <button className="ap-btn ap-btn--primary" type="submit" disabled={partnersBusy}>
                      {partnersBusy ? 'Adding...' : 'Add partner'}
                    </button>
                  </div>
                </form>
              </section>
            ) : null}

            {isAdmin ? (
              <section className="ap-card">
                <div className="ap-card-head">
                  <h3 className="ap-card-title">Add Certification for Member</h3>
                </div>

                <form onSubmit={onAdminAddCertification} className="ap-form ap-form--medium">
                  <div className="ap-field">
                    <label className="ap-label">Member</label>
                    <select
                      className="ap-select"
                      value={adminMemberId}
                      onChange={(e) => setAdminMemberId(e.target.value)}
                      disabled={partnersBusy}
                      required
                    >
                      <option value="">Select member</option>
                      {memberUsers.map((member) => (
                        <option key={member.id} value={member.id}>
                          {member.name || member.email || 'Unnamed member'}{member.email ? ` — ${member.email}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Certification</label>
                    <select
                      className="ap-select"
                      value={adminCertTypeId}
                      onChange={(e) => setAdminCertTypeId(e.target.value)}
                      disabled={partnersBusy}
                      required
                    >
                      <option value="">Select certification</option>
                      {partners.flatMap((partner) => (partner.certificationTypes || []).map((type) => (
                        <option key={type.id} value={type.id}>
                          {partner.name} — {type.name}
                        </option>
                      )))}
                    </select>
                  </div>

                  <div className="ap-field">
                    <label className="ap-label">Completion date</label>
                    <input
                      className="ap-input"
                      type="date"
                      value={adminCompletedDate}
                      onChange={(e) => setAdminCompletedDate(e.target.value)}
                      disabled={partnersBusy}
                      required
                    />
                  </div>

                  <div className="ap-form-actions">
                    <button className="ap-btn ap-btn--primary" type="submit" disabled={partnersBusy || !memberUsers.length}>
                      {partnersBusy ? 'Saving...' : 'Add member certification'}
                    </button>
                    <button className="ap-btn" type="button" onClick={resetAdminCertificationForm} disabled={partnersBusy}>
                      Clear
                    </button>
                  </div>
                </form>
                {!memberUsers.length ? <div className="ap-row-meta" style={{ marginTop: 10 }}>No MEMBER accounts found.</div> : null}
              </section>
            ) : null}

            <section className="ap-card">
              <div className="ap-card-head">
                <h3 className="ap-card-title">Partners &amp; Certifications</h3>
                <button className="ap-btn" type="button" onClick={loadPartnersAndCerts} disabled={partnersBusy}>
                  Refresh
                </button>
              </div>

              <div className="ap-list">
                {partners.map((p) => (
                  <div key={p.id} className="ap-row">
                    <div className="ap-row-top">
                      <div>
                        <div className="ap-row-name">{p.name}</div>
                        <div className="ap-row-meta">
                          Status: {p.status} {p.path ? `· ${p.path}` : ''}
                        </div>
                      </div>

                      <div className="ap-row-actions">
                        <button
                          className="ap-btn"
                          type="button"
                          disabled={partnersBusy}
                          aria-expanded={expandedPartnerId === p.id}
                          onClick={() => {
                            setExpandedPartnerId(
                              expandedPartnerId === p.id ? null : p.id
                            );
                            setCertTypeFormFor(null);
                            setEditingCertId(null);
                            setEditingMemberCertId(null);
                          }}
                        >
                          {expandedPartnerId === p.id ? 'Close' : 'View certifications'}
                        </button>

                        {isAdmin ? (
                          <>
                            <button
                              className="ap-btn"
                              type="button"
                              disabled={partnersBusy}
                              onClick={() => {
                                setExpandedPartnerId(p.id);
                                setCertTypeFormFor(certTypeFormFor === p.id ? null : p.id);
                              }}
                            >
                              {certTypeFormFor === p.id ? 'Cancel' : 'Add certification'}
                            </button>
                            <button
                              className="ap-btn"
                              type="button"
                              disabled={partnersBusy}
                              onClick={() =>
                                editingPartnerId === p.id
                                  ? cancelEditPartner()
                                  : startEditPartner(p)
                              }
                            >
                              {editingPartnerId === p.id ? 'Cancel edit' : 'Edit partner'}
                            </button>
                            <button
                              className="ap-btn ap-btn--danger"
                              type="button"
                              disabled={partnersBusy}
                              onClick={() => onDeletePartner(p.id)}
                            >
                              Delete partner
                            </button>
                          </>
                        ) : null}
                      </div>
                    </div>

                    {isAdmin && editingPartnerId === p.id ? (
                      <form
                        onSubmit={(e) => onUpdatePartner(e, p.id)}
                        className="ap-form ap-form--narrow ap-subcard"
                      >
                        <div className="ap-label" style={{ fontSize: 11 }}>Edit partner</div>

                        <div className="ap-field">
                          <label className="ap-label">Partner name</label>
                          <input
                            className="ap-input"
                            value={editPartnerName}
                            onChange={(e) => setEditPartnerName(e.target.value)}
                            required
                            disabled={partnersBusy}
                          />
                        </div>

                        <div className="ap-field">
                          <label className="ap-label">Logo URL</label>
                          <input
                            className="ap-input"
                            value={editPartnerLogo}
                            onChange={(e) => setEditPartnerLogo(e.target.value)}
                            disabled={partnersBusy}
                          />
                        </div>

                        <div className="ap-field">
                          <label className="ap-label">Public page path</label>
                          <input
                            className="ap-input"
                            value={editPartnerPath}
                            onChange={(e) => setEditPartnerPath(e.target.value)}
                            placeholder="/partners/Cisco"
                            disabled={partnersBusy}
                          />
                        </div>

                        <div className="ap-field">
                          <label className="ap-label">Status</label>
                          <select
                            className="ap-select"
                            value={editPartnerStatus}
                            onChange={(e) => setEditPartnerStatus(e.target.value)}
                            disabled={partnersBusy}
                          >
                            <option value="ACTIVE">ACTIVE</option>
                            <option value="INACTIVE">INACTIVE</option>
                          </select>
                        </div>

                        <div className="ap-form-actions">
                          <button className="ap-btn ap-btn--primary" type="submit" disabled={partnersBusy}>
                            {partnersBusy ? 'Saving...' : 'Save partner'}
                          </button>
                          <button className="ap-btn" type="button" onClick={cancelEditPartner} disabled={partnersBusy}>
                            Cancel
                          </button>
                        </div>
                      </form>
                    ) : null}

                    {expandedPartnerId === p.id ? (
                      <div className="ap-row-sub">
                        {isAdmin && certTypeFormFor === p.id ? (
                          <form
                            onSubmit={(e) => onAddCertType(e, p.id)}
                            className="ap-form ap-form--narrow ap-subcard"
                            style={{ marginTop: 0 }}
                          >
                            <div className="ap-field">
                              <label className="ap-label">Certification name</label>
                              <input
                                className="ap-input"
                                value={newCertName}
                                onChange={(e) => setNewCertName(e.target.value)}
                                placeholder="e.g. Fortinet NSE 4"
                                required
                                disabled={partnersBusy}
                              />
                            </div>

                            <div className="ap-field">
                              <label className="ap-label">Validity (months, optional)</label>
                              <input
                                className="ap-input"
                                type="number"
                                min="1"
                                value={newCertValidity}
                                onChange={(e) => setNewCertValidity(e.target.value)}
                                placeholder="e.g. 24"
                                disabled={partnersBusy}
                              />
                            </div>

                            <div className="ap-form-actions">
                              <button className="ap-btn ap-btn--primary" type="submit" disabled={partnersBusy}>
                                {partnersBusy ? 'Adding...' : 'Add certification type'}
                              </button>
                            </div>
                          </form>
                        ) : null}

                        {p.certificationTypes?.length ? (
                          <div style={{ marginTop: certTypeFormFor === p.id ? 14 : 0 }}>
                            {p.certificationTypes.map((ct) => {
                              const holders = certsForType(ct.id);
                              return (
                                <div key={ct.id} className="ap-cert-type-row">
                                  <div className="ap-cert-type-top">
                                    <div className="ap-cert-type-name">
                                      {ct.name}
                                      {ct.validityMonths ? ` (valid ${ct.validityMonths} mo)` : ''}
                                    </div>

                                    {isAdmin ? (
                                      <div className="ap-row-actions">
                                        <button
                                          className="ap-btn ap-btn--sm"
                                          type="button"
                                          disabled={partnersBusy}
                                          onClick={() =>
                                            editingCertId === ct.id
                                              ? cancelEditCert()
                                              : startEditCert(ct)
                                          }
                                        >
                                          {editingCertId === ct.id ? 'Cancel edit' : 'Edit'}
                                        </button>
                                        <button
                                          className="ap-btn ap-btn--sm ap-btn--danger"
                                          type="button"
                                          disabled={partnersBusy}
                                          onClick={() => onDeleteCertType(ct.id)}
                                        >
                                          Delete
                                        </button>
                                      </div>
                                    ) : null}
                                  </div>

                                  {isAdmin && editingCertId === ct.id ? (
                                    <form
                                      onSubmit={(e) => onUpdateCertType(e, ct.id)}
                                      className="ap-form ap-form--narrow ap-subcard"
                                    >
                                      <div className="ap-field">
                                        <label className="ap-label">Certification name</label>
                                        <input
                                          className="ap-input"
                                          value={editCertName}
                                          onChange={(e) => setEditCertName(e.target.value)}
                                          required
                                          disabled={partnersBusy}
                                        />
                                      </div>

                                      <div className="ap-field">
                                        <label className="ap-label">Validity (months)</label>
                                        <input
                                          className="ap-input"
                                          type="number"
                                          min="1"
                                          value={editCertValidity}
                                          onChange={(e) => setEditCertValidity(e.target.value)}
                                          placeholder="Leave empty for no expiry"
                                          disabled={partnersBusy}
                                        />
                                      </div>

                                      <div className="ap-form-actions">
                                        <button className="ap-btn ap-btn--primary" type="submit" disabled={partnersBusy}>
                                          {partnersBusy ? 'Saving...' : 'Save certification'}
                                        </button>
                                        <button className="ap-btn" type="button" onClick={cancelEditCert} disabled={partnersBusy}>
                                          Cancel
                                        </button>
                                      </div>
                                    </form>
                                  ) : null}

                                  {isAdmin ? (
                                    <div className="ap-holder-list">
                                      {holders.length === 0 ? (
                                        <div className="ap-row-meta">No one certified yet.</div>
                                      ) : (
                                        holders.map((c) => {
                                          const isEditing = editingMemberCertId === c.id;
                                          const completed = new Date(c.completedDate);
                                          const expiry = c.expiryDate ? new Date(c.expiryDate) : null;

                                          return (
                                            <div key={c.id} className="ap-holder-row">
                                              <div className="ap-holder-top">
                                                <span className="ap-holder-meta">
                                                  {c.user?.email || c.user?.name || 'Member'} — completed{' '}
                                                  {Number.isNaN(completed.getTime()) ? '—' : completed.toLocaleDateString()}
                                                  {expiry && !Number.isNaN(expiry.getTime())
                                                    ? ` — expires ${expiry.toLocaleDateString()}`
                                                    : ''}
                                                </span>
                                                <div className="ap-row-actions">
                                                  <span className={statusBadgeClass(c.status)}>
                                                    {String(c.status || 'ACTIVE').replace('_', ' ')}
                                                  </span>
                                                  <button
                                                    className="ap-btn ap-btn--sm"
                                                    type="button"
                                                    disabled={partnersBusy}
                                                    onClick={() =>
                                                      isEditing
                                                        ? cancelEditMemberCertification()
                                                        : startEditMemberCertification(c)
                                                    }
                                                  >
                                                    {isEditing ? 'Cancel' : 'Edit'}
                                                  </button>
                                                  <button
                                                    className="ap-btn ap-btn--sm ap-btn--danger"
                                                    type="button"
                                                    disabled={partnersBusy}
                                                    onClick={() => onDeleteMemberCertification(c)}
                                                  >
                                                    Delete
                                                  </button>
                                                </div>
                                              </div>

                                              {isEditing ? (
                                                <div className="ap-holder-edit">
                                                  <div className="ap-field">
                                                    <label className="ap-label">Completion date</label>
                                                    <input
                                                      className="ap-input"
                                                      type="date"
                                                      value={editMemberCertDate}
                                                      onChange={(e) => setEditMemberCertDate(e.target.value)}
                                                      disabled={partnersBusy}
                                                    />
                                                  </div>
                                                  <button
                                                    className="ap-btn ap-btn--primary"
                                                    type="button"
                                                    disabled={partnersBusy || !editMemberCertDate}
                                                    onClick={() => onUpdateMemberCertification(c.id)}
                                                  >
                                                    {partnersBusy ? 'Saving...' : 'Save date'}
                                                  </button>
                                                </div>
                                              ) : null}
                                            </div>
                                          );
                                        })
                                      )}
                                    </div>
                                  ) : null}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="ap-row-meta">
                            No certification types added for this partner yet.
                          </div>
                        )}
                      </div>
                    ) : null}
                  </div>
                ))}

                {partners.length === 0 ? <div className="ap-empty">No partners found.</div> : null}
              </div>
            </section>
          </>
        ) : null}

        {tab === 'blogs' ? (
          <section className="ap-card">
            <div className="ap-card-head">
              <h3 className="ap-card-title">Blog Management</h3>
            </div>

            <div className="ap-link-grid">
              <Link to="/admin/blogs" className="ap-link-card">
                <h4>Manage Blogs</h4>
                <p>View, edit, publish, archive, or delete existing blog posts.</p>
              </Link>

              <Link to="/admin/blogs/create" className="ap-link-card">
                <h4>Create Blog</h4>
                <p>Write a new blog post and publish it to the website.</p>
              </Link>
            </div>
          </section>
        ) : null}

        {tab === 'support' ? (
          <section className="ap-card">
            <div className="ap-card-head">
              <h3 className="ap-card-title">Support Content</h3>
            </div>

            <div className="ap-link-grid">
              <Link to="/admin/support" className="ap-link-card">
                <h4>Manage Support Content</h4>
                <p>Add, edit, or disable FAQ categories and questions shown in the support chat widget.</p>
              </Link>
            </div>
          </section>
        ) : null}
      </div>
    </div>
  );
}